import SendIcon from '@mui/icons-material/Send';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import languages from '../../../../../../helpers/utils/languages';
import useGetSavOrdersByOrderId from '../../../../../../hooks/orders/useGetSavOrdersByOrderId';
import useResentPaymentLink from '../../../../../../hooks/orders/useResentPaymentLink';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import UpdateSavShipping from './UpdateSavShipping';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ManageSav = ({
  orderDetails,
  setSnackBarProps,
  handleShowComponent,
  profile,
}) => {
  const users = useSelector((state) => state.static.users);
  const [currentSavOrderRef, setCurrentSavOrderRef] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const orderId = useMemo(() => orderDetails._id, [orderDetails]);

  const { savOrders, isLoading, error } = useGetSavOrdersByOrderId(orderId);
  const { resentPaymentLink } = useResentPaymentLink();

  const getAuthorName = (savDatasFromBO) => {
    const user = users?.find(
      (user) => user.email === savDatasFromBO.created_by
    );
    return user
      ? `${user.firstName} ${user.lastName}`
      : savDatasFromBO.created_by;
  };

  const getCountryName = (countryCode) => {
    return (
      languages.find((country) => country.code2 === countryCode)?.country ||
      countryCode
    );
  };

  const handleChangeTab = (event, newValue) => {
    console.log('newValue', newValue);
    setCurrentSavOrderRef(newValue);
  };

  const handleResentaymentLink = async (savOrderRef) => {
    try {
      setEmailSending(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await resentPaymentLink(savOrderRef, {
        ticketRef: orderDetails?.ticket?.reference,
        author: profile?._id,
      });
      setEmailSending(false);
      setSnackBarProps({
        isOpen: true,
        message: "L'email avec le lien de paiement a été renvoyé avec succès",
        variant: 'success',
      });
    } catch (error) {
      console.error(error);
      setEmailSending(false);
      setSnackBarProps({
        isOpen: true,
        message: "Une erreur est survenue lors du renvoi de l'email",
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (isLoading || !savOrders?.length) return;
    setCurrentSavOrderRef(savOrders[0].reference);
  }, [savOrders, isLoading]);

  if (isLoading || error || !savOrders?.length || !currentSavOrderRef) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card
        elevation={5}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <SettingsApplicationsIcon
              style={{ color: '#10407B' }}
              fontSize="small"
            />
            <Typography color="primary">Gérer les SAVs</Typography>
          </Box>
          <Button
            onClick={() => handleShowComponent(null)}
            variant="outlined"
            size="small"
            sx={{ position: 'absolute', top: 16, left: 16 }}
          >
            <ArrowReturnIcon fontSize="small" />
            <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
          </Button>
        </Box>
      </Card>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentSavOrderRef} onChange={handleChangeTab}>
            {savOrders?.map((savOrder) => (
              <Tab
                key={savOrder.reference}
                label={savOrder.reference}
                value={savOrder.reference}
                {...a11yProps(savOrder.reference)}
              />
            ))}
          </Tabs>
        </Box>
        {savOrders.map((savOrder) => (
          <CustomTabPanel
            value={currentSavOrderRef}
            index={savOrder.reference}
            key={savOrder.reference}
          >
            <Card elevation={5} sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 'bold' }}
                >
                  {savOrder?.savDatasFromBO?.reference}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={
                      savOrder.savDatasFromBO.is_payable
                        ? `SAV Payante ${savOrder.savDatasFromBO.amount_ttc} ${getSymbolFromCurrency(
                            savOrder.savDatasFromBO.currency
                          )}`
                        : 'SAV Gratuite'
                    }
                    color={
                      savOrder.savDatasFromBO.is_payable ? 'warning' : 'success'
                    }
                    variant="outlined"
                  />
                  {savOrder?.paymentStatus && (
                    <Chip
                      label={savOrder.paymentStatus.name || 'Statut inconnu'}
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography variant="body2">
                  Créée par{' '}
                  <strong>{getAuthorName(savOrder.savDatasFromBO)}</strong>
                </Typography>
                <Typography variant="body2">
                  le{' '}
                  {format(
                    new Date(savOrder.savDatasFromBO.created_at),
                    'dd MMMM yyyy à HH:mm',
                    {
                      locale: fr,
                    }
                  )}
                </Typography>
              </Box>

              {savOrder.savDatasFromBO.is_payable && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Montant :{' '}
                    <strong>
                      {savOrder.savDatasFromBO.amount_ttc}{' '}
                      {savOrder.savDatasFromBO.currency.toUpperCase()}
                    </strong>
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>
                Pièces / colis
              </Typography>
              <Box sx={{ mb: 2 }}>
                {savOrder.savDatasFromBO.lines.map((line, index) => (
                  <Typography key={index} variant="body2">
                    {`${line.quantity} x `}
                    <strong>{line.reference}</strong>
                    {line?.title && ` - ${line?.title}`}
                  </Typography>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Adresse de livraison
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.first_name}{' '}
                    {savOrder.savDatasFromBO.shipping_address.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.address_1}
                  </Typography>
                  {savOrder.savDatasFromBO.shipping_address.address_2 && (
                    <Typography variant="body2">
                      {savOrder.savDatasFromBO.shipping_address.address_2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.postal_code}{' '}
                    {savOrder.savDatasFromBO.shipping_address.city}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.country && (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <span
                          className={`fi fi-${savOrder.savDatasFromBO.shipping_address.country?.toLowerCase() || ''}`}
                        />
                        <Typography variant="body2">
                          {getCountryName(
                            savOrder.savDatasFromBO.shipping_address.country
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.shipping_address.phone}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Adresse de facturation
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.first_name}{' '}
                    {savOrder.savDatasFromBO.billing_address.last_name}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.address_1}
                  </Typography>
                  {savOrder.savDatasFromBO.billing_address.address_2 && (
                    <Typography variant="body2">
                      {savOrder.savDatasFromBO.billing_address.address_2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.postal_code}{' '}
                    {savOrder.savDatasFromBO.billing_address.city}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.country && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <span
                          className={`fi fi-${savOrder.savDatasFromBO.billing_address.country?.toLowerCase() || ''}`}
                        />
                        {getCountryName(
                          savOrder.savDatasFromBO.billing_address.country
                        )}
                      </Box>
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {savOrder.savDatasFromBO.billing_address.phone}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <UpdateSavShipping
                savDatasFromBO={savOrder.savDatasFromBO}
                setSnackBarProps={setSnackBarProps}
                profile={profile}
                ticket={orderDetails?.ticket}
              />
              <Divider sx={{ my: 2 }} />
              {savOrder.savDatasFromBO.is_payable &&
                savOrder?.paymentStatus?.boV2Code !== 'payment_free' &&
                savOrder?.paymentStatus?.boV2Code !== 'payment_captured' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{ fontWeight: 'bold' }}
                        gutterBottom
                      >
                        Lien de paiement
                      </Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        endIcon={
                          emailSending ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SendIcon />
                          )
                        }
                        disabled={
                          emailSending ||
                          savOrder?.paymentStatus?.boV2Code ===
                            'payment_captured'
                        }
                        onClick={() =>
                          handleResentaymentLink(savOrder.reference)
                        }
                      >
                        Renvoyer le lien de paiement
                      </Button>
                    </Box>
                  </>
                )}

              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}
              >
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => handleShowComponent(null)}
                >
                  Annuler
                </Button>
              </Box>
            </Card>
          </CustomTabPanel>
        ))}
      </Box>
    </>
  );
};

export default ManageSav;
